import React, { useState, useEffect } from 'react';
import { Menu, X, Phone, Mail } from 'lucide-react';

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const checkScroll = () => {
      const scrolled = window.scrollY > 24;
      setIsScrolled(prev => (prev !== scrolled ? scrolled : prev));
    };

    checkScroll();

    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          checkScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navigation = [
    { name: 'Inicio', href: '#home' },
    { name: 'Nosotros', href: '#about' },
    { name: 'Servicios', href: '#services' },
    { name: 'Tecnología', href: '#technology' },
    { name: 'Alianzas', href: '#alianzas' },
  ];

  return (
    <header className="fixed top-0 inset-x-0 z-50">
      {/* Slim utility strip */}
      <div
        className={`overflow-hidden transition-all duration-500 ${
          isScrolled ? 'max-h-0 opacity-0' : 'max-h-10 opacity-100'
        }`}
      >
        <div className="border-b border-white/[0.06] bg-secondary-900/70 backdrop-blur-md">
          <div className="container-custom">
            <div className="flex justify-between items-center text-[12px] tracking-wide text-gray-400 py-1.5">
              <div className="flex items-center gap-5">
                <a
                  href="https://wa.me/5491131891414"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 hover:text-accent-400 transition-colors duration-200"
                >
                  <Phone className="w-3 h-3 text-accent-500" />
                  <span>(+54) 11 3189 1414</span>
                </a>
                <a
                  href="mailto:hello@hbeeit.com"
                  className="hidden sm:flex items-center gap-1.5 hover:text-accent-400 transition-colors duration-200"
                >
                  <Mail className="w-3 h-3 text-accent-500" />
                  <span>hello@hbeeit.com</span>
                </a>
              </div>
              <span className="hidden md:inline text-gray-500 uppercase tracking-[0.18em] text-[10px]">
                Cloud · DevSecOps
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Main bar */}
      <nav
        className={`transition-all duration-500 border-b ${
          isScrolled
            ? 'bg-secondary-900/85 backdrop-blur-xl border-accent-500/20 shadow-[0_8px_32px_rgba(0,0,0,0.45)]'
            : 'bg-secondary-900/35 backdrop-blur-md border-white/[0.06]'
        }`}
      >
        {/* Orange hairline */}
        <div
          className={`h-px bg-gradient-to-r from-transparent via-accent-500/70 to-transparent transition-opacity duration-500 ${
            isScrolled ? 'opacity-100' : 'opacity-40'
          }`}
        />

        <div className="container-custom">
          <div
            className={`flex justify-between items-center transition-all duration-500 ${
              isScrolled ? 'py-2.5' : 'py-3.5'
            }`}
          >
            {/* Brand */}
            <a href="#home" className="flex items-center gap-3 group min-w-0">
              <div className="relative w-10 h-10 flex items-center justify-center flex-shrink-0">
                <div className="absolute inset-0 rounded-lg bg-accent-500/10 border border-accent-500/20 group-hover:border-accent-500/50 transition-colors duration-300" />
                <img
                  src="/logo.png"
                  alt="Hybrid Bee Technology"
                  className="relative max-w-[70%] max-h-[70%] object-contain"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    const nextElement = e.currentTarget.nextElementSibling as HTMLElement;
                    if (nextElement) nextElement.style.display = 'flex';
                  }}
                />
                <div className="w-8 h-8 bg-accent-500 rounded-md items-center justify-center hidden">
                  <span className="text-white font-bold text-sm">HBT</span>
                </div>
              </div>
              <div className="min-w-0">
                <div className="text-[15px] md:text-base font-display font-semibold text-white tracking-tight truncate group-hover:text-accent-300 transition-colors duration-300">
                  Hybrid Bee Technology
                </div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500 group-hover:text-accent-500/80 transition-colors duration-300">
                  Cloud & DevSecOps
                </div>
              </div>
            </a>

            {/* Desktop nav */}
            <div className="hidden lg:flex items-center gap-1">
              <div className="flex items-center gap-0.5 rounded-full border border-white/[0.06] bg-white/[0.03] px-1.5 py-1 mr-3">
                {navigation.map((item) => (
                  <a
                    key={item.name}
                    href={item.href}
                    className="nav-link relative px-4 py-2 text-sm font-medium text-gray-300 hover:text-white transition-colors duration-200"
                  >
                    {item.name}
                  </a>
                ))}
              </div>
              <a
                href="#contact"
                className="relative inline-flex items-center justify-center overflow-hidden rounded-full bg-accent-500 px-5 py-2.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-accent-600 hover:shadow-[0_0_24px_rgba(231,111,42,0.35)]"
              >
                Contacto
              </a>
            </div>

            {/* Mobile toggle */}
            <button
              type="button"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-lg border border-white/10 bg-white/[0.03] text-gray-300 hover:text-accent-400 hover:border-accent-500/40 transition-colors duration-200"
              aria-label={isMenuOpen ? 'Cerrar menú' : 'Abrir menú'}
            >
              {isMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>

          {/* Mobile panel */}
          <div
            className={`lg:hidden overflow-hidden transition-all duration-300 ${
              isMenuOpen ? 'max-h-80 opacity-100 pb-4' : 'max-h-0 opacity-0'
            }`}
          >
            <div className="rounded-2xl border border-border bg-surface-raised/90 backdrop-blur-xl p-3 space-y-1">
              {navigation.map((item) => (
                <a
                  key={item.name}
                  href={item.href}
                  className="block rounded-xl px-4 py-3 text-sm font-medium text-gray-300 hover:text-white hover:bg-accent-500/10 transition-colors duration-200"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.name}
                </a>
              ))}
              <a
                href="#contact"
                className="btn-primary w-full mt-2 text-center block"
                onClick={() => setIsMenuOpen(false)}
              >
                Contacto
              </a>
            </div>
          </div>
        </div>
      </nav>
    </header>
  );
};

export default Header;
